<template>
    <div>
        <Breadcrumb :style="{ margin: '20px 10px' }">
            <BreadcrumbItem>权限管理</BreadcrumbItem>
            <BreadcrumbItem>权限列表</BreadcrumbItem>
        </Breadcrumb>
        <loading :loading="loading" :error="error"></loading>
        <div v-if="permissions" class="content">
            <!-- 使用 Tree 组件展示权限 -->
            <div class="permission-tree">
                <div class="tree-header">
                    <Row>
                        <Col span="5"><strong>权限名称</strong></Col>
                        <Col span="3"><strong>守卫</strong></Col>
                        <Col span="4"><strong>前端网址</strong></Col>
                        <Col span="4"><strong>路由别名</strong></Col>
                        <Col span="2"><strong>隐藏</strong></Col>
                        <Col span="2"><strong>父ID</strong></Col>
                        <Col span="2"><strong>级别</strong></Col>
                        <Col span="2"><strong>操作</strong></Col>
                    </Row>
                </div>
                
                <div class="tree-content">
                    <template v-for="permission in permissions">
                        <!-- 渲染顶级权限 -->
                        <div :key="permission.id" class="tree-node level-0">
                            <Row>
                                <Col span="5">
                                    <div style="padding-left: 20px;">
                                        <Icon 
                                            v-if="permission.children && permission.children.length > 0"
                                            :type="permission._expanded ? 'ios-arrow-down' : 'ios-arrow-forward'"
                                            @click="toggleExpand(permission)"
                                            style="cursor: pointer; margin-right: 5px;"
                                        />
                                        <span v-else style="display: inline-block; width: 18px; margin-right: 5px;"></span>
                                        <span>{{ permission.name }}</span>
                                    </div>
                                </Col>
                                <Col span="3">{{ permission.guard_name }}</Col>
                                <Col span="4">{{ permission.url || '—' }}</Col>
                                <Col span="4">{{ permission.route || '—' }}</Col>
                                <Col span="2">{{ permission.is_hide === 1 ? '是' : '否' }}</Col>
                                <Col span="2">{{ permission.pid }}</Col>
                                <Col span="2">{{ permission.level }}</Col>
                                <Col span="2">
                                    <Button type="primary" size="small" @click="handleEdit(permission)">修改</Button>
                                </Col>
                            </Row>
                        </div>
                        
                        <!-- 渲染子权限 -->
                        <template v-if="permission.children && permission.children.length > 0 && permission._expanded">
                            <div 
                                v-for="child in permission.children" 
                                :key="child.id" 
                                class="tree-node level-1"
                            >
                                <Row>
                                    <Col span="5">
                                        <div style="padding-left: 50px;">
                                            <span style="display: inline-block; width: 18px; margin-right: 5px;"></span>
                                            {{ child.name }}
                                        </div>
                                    </Col>
                                    <Col span="3">{{ child.guard_name }}</Col>
                                    <Col span="4">{{ child.url || '—' }}</Col>
                                    <Col span="4">{{ child.route || '—' }}</Col>
                                    <Col span="2">{{ child.is_hide === 1 ? '是' : '否' }}</Col>
                                    <Col span="2">{{ child.pid }}</Col>
                                    <Col span="2">{{ child.level }}</Col>
                                    <Col span="2">
                                        <Button type="primary" size="small" @click="handleEdit(child)">修改</Button>
                                    </Col>
                                </Row>
                            </div>
                        </template>
                    </template>
                </div>
            </div>
            
            <!-- 编辑模态框 -->
            <Modal
                v-model="showEditModal"
                title="编辑权限"
                @on-ok="handleSave"
                @on-cancel="cancelEdit"
            >
                <Form :model="editForm" :label-width="80">
                    <FormItem label="权限名称">
                        <Input v-model="editForm.name" placeholder="请输入权限名称" />
                    </FormItem>
                    <FormItem label="前端网址">
                        <Input v-model="editForm.url" placeholder="请输入前端网址" />
                    </FormItem>
                    <FormItem label="路由别名">
                        <Input v-model="editForm.route" placeholder="请输入路由别名" />
                    </FormItem>
                    <FormItem label="是否隐藏">
                        <Select v-model="editForm.is_hide">
                            <Option value="0">否</Option>
                            <Option value="1">是</Option>
                        </Select>
                    </FormItem>
                    <FormItem label="父ID">
                        <InputNumber v-model="editForm.pid" :min="0" style="width: 100%" />
                    </FormItem>
                    <FormItem label="级别">
                        <InputNumber v-model="editForm.level" :min="0" style="width: 100%" />
                    </FormItem>
                </Form>
            </Modal>
        </div>
    </div>
</template>

<script>
import loading from '../loading.vue';

export default {
    components: { loading },
    data() {
        return {
            loading: false,
            permissions: [],
            error: null,
            showEditModal: false,
            editingNode: null,
            editForm: {
                id: -1,
                name: '',
                url: '',
                route: '',
                is_hide: '0',
                pid: 0,
                level: 0
            }
        };
    },
    created() {
        this.fetchData();
    },
    watch: {
        $route: "fetchData",
    },
    methods: {
        // 切换展开/折叠
        toggleExpand(permission) {
            if (!permission._expanded) {
                this.$set(permission, '_expanded', true);
            } else {
                this.$set(permission, '_expanded', false);
            }
        },
        
        // 处理编辑
        handleEdit(node) {
            this.editingNode = node;
            this.editForm = {
                id: node.id,
                name: node.name,
                url: node.url || '',
                route: node.route || '',
                is_hide: String(node.is_hide),
                pid: node.pid,
                level: node.level
            };
            this.showEditModal = true;
        },
        
        // 取消编辑
        cancelEdit() {
            this.showEditModal = false;
            this.editingNode = null;
            this.editForm = {
                id: -1,
                name: '',
                url: '',
                route: '',
                is_hide: '0',
                pid: 0,
                level: 0
            };
        },
        
        // 处理保存
        handleSave() {
            this.loading = true;
            const data = {
                name: this.editForm.name,
                route: this.editForm.route,
                url: this.editForm.url,
                is_hide: parseInt(this.editForm.is_hide),
                pid: this.editForm.pid,
                level: this.editForm.level,
            };
            
            this.axios.patch('permissions/' + this.editForm.id, data)
                .then((res) => {
                    this.loading = false;
                    if (res.data.status === 200) {
                        // 更新节点数据
                        Object.assign(this.editingNode, data);
                        this.$Message.success("修改成功");
                        this.cancelEdit();
                    } else {
                        this.$Message.error(res.data.msg);
                    }
                })
                .catch(error => {
                    this.loading = false;
                    this.$Message.error("修改失败");
                    console.error("修改权限失败:", error);
                });
        },
        
        fetchData() {
            this.error = null;
            this.loading = true;
            this.axios.get("permissions")
                .then((res) => {
                    if (res.data.permissions && Array.isArray(res.data.permissions)) {
                        // 处理数据，添加 _expanded 属性用于控制展开
                        this.permissions = res.data.permissions.map(permission => ({
                            ...permission,
                            _expanded: false
                        }));
                    } else {
                        this.permissions = [];
                    }
                    this.loading = false;
                })
                .catch(error => {
                    this.loading = false;
                    this.error = error;
                    this.$Message.error("加载权限列表失败");
                    console.error("加载权限列表失败:", error);
                });
        }
    }
};
</script>

<style scoped>
.permission-tree {
    border: 1px solid #dcdee2;
    border-radius: 4px;
    background: #fff;
}

.tree-header {
    background: #f8f8f9;
    padding: 12px 16px;
    border-bottom: 1px solid #dcdee2;
}

.tree-content {
    padding: 0;
}

.tree-node {
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.2s;
}

.tree-node:hover {
    background-color: #f5f5f5;
}

.tree-node.level-0 {
    font-weight: 500;
}

.tree-node.level-1 {
    background-color: #fafafa;
}

.tree-node:last-child {
    border-bottom: none;
}
</style>